import React, { useContext } from 'react';
import { AuthObject } from '../utils/frontEndTypes';
import { AuthContext } from './AuthContext';
import { ConfigContext } from './ConfigContext';
import { QueryApiContext } from './QueryContext';
import { signedFetch } from '../utils/signedFetch';

const INITIAL_LOADING = {
  loading: true,
  error: false
};

const LOGGED_OUT = {
  loading: false,
  error: true
};

type AuthProviderProps = {
  children: React.ReactNode;
};

interface ApiUser {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string | Date;
  roles: Array<{
    id: string;
    userId: string;
    roleTypeId: string;
    scopeId: string | null;
  }>;
}

function hasLoggedInCookie(): boolean {
  return document.cookie.split(';').some((c) => c.trim().startsWith('logged_in='));
}

const extractAuthInformation = (user: ApiUser | null, apiUrl: string) => {
  if (user === null) {
    return INITIAL_LOADING;
  }

  const groups = user.roles.map((role) => role.scopeId || '').filter(Boolean);

  const auth: AuthObject = {
    groups: groups,
    user: user,
    signOut: async () => {
      try {
        await signedFetch(`${apiUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch {
        // Ignore logout errors
      }
      window.location.href = '/';
    },
    loading: false,
    error: false
  };
  return auth;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const configContext = useContext(ConfigContext);

  // Wait for config to load
  if (!configContext?.config) {
    return <AuthContext.Provider value={INITIAL_LOADING}>{children}</AuthContext.Provider>;
  }

  const apiUrl = configContext.config.api.apiUrl;

  // If no logged_in cookie, skip the API call and go straight to sign-in
  if (!hasLoggedInCookie()) {
    return <AuthContext.Provider value={LOGGED_OUT}>{children}</AuthContext.Provider>;
  }

  return <AuthProviderInner apiUrl={apiUrl}>{children}</AuthProviderInner>;
};

/**
 * Inner component that only renders when we have a logged_in cookie.
 * Fetches user data to confirm auth is valid.
 */
const AuthProviderInner: React.FC<{ children: React.ReactNode; apiUrl: string }> = ({ children, apiUrl }) => {
  const apiClient = useContext(QueryApiContext);

  const userQuery = apiClient.users.me.useQuery(['users', 'me'], {}, { retry: false, enabled: true });

  let authInformation: AuthObject | typeof INITIAL_LOADING | typeof LOGGED_OUT;

  if (userQuery.isLoading) {
    authInformation = INITIAL_LOADING;
  } else if (userQuery.isError || !userQuery.data?.body) {
    authInformation = LOGGED_OUT;
  } else {
    const userData = userQuery.data.body;
    const apiUser: ApiUser = {
      ...userData
    };
    authInformation = extractAuthInformation(apiUser, apiUrl);
  }

  return <AuthContext.Provider value={authInformation}>{children}</AuthContext.Provider>;
};
